// console-sections.ts — single frontend source of truth for Console IA
// section → platform-role access, mirroring the backend authority in
// `backend/src/shared/platform_rbac.rs` (`section_access`).
//
// U3 (§7.3.27) — Console IA / deep-link permissions.
//
// The backend is the ONLY real authorization boundary: every gated Console
// endpoint calls `auth.require_section(ConsoleSection::X, Access::Y)` and returns
// 403 when the caller's platform roles do not grant it. This module is the
// front-end MIRROR used for two UX-layer concerns:
//   1. Sidebar visibility (ConsoleLayout consumes `hasSectionAccess`).
//   2. Deep-link guarding (router guard sends a section-denied navigation to the
//      explicit /403 forbidden page instead of silently rendering a page whose
//      data calls will 403 — or silently redirecting away).
//
// `SECTION_ROLES` is the Read-or-better projection of the backend 5×13 matrix:
// a platform role appears for a section iff `section_access(role, section) >=
// Read`. Keeping this in ONE place (consumed by sidebar + guard + the U3
// permission-matrix evidence script) is what prevents sidebar/guard drift.

/** Canonical Console section ids (kebab). 1:1 with backend `ConsoleSection`. */
export const CONSOLE_SECTIONS = [
  'overview',
  'my-clients',
  'monitoring',
  'ops-users',
  'finance',
  'perf-audit',
  'strategy',
  'controlled-ops',
  'infra',
  'catalog',
  'allowance-params',
  'system-settings',
  'roles-tenants',
] as const

export type ConsoleSectionId = (typeof CONSOLE_SECTIONS)[number]

/** Kebab section id → backend `ConsoleSection` enum variant (audit cross-ref). */
export const SECTION_TO_BACKEND_ENUM: Record<ConsoleSectionId, string> = {
  'overview': 'Overview',
  'my-clients': 'MyClients',
  'monitoring': 'Monitoring',
  'ops-users': 'OpsUsers',
  'finance': 'Finance',
  'perf-audit': 'PerfAudit',
  'strategy': 'Strategy',
  'controlled-ops': 'ControlledOps',
  'infra': 'Infra',
  'catalog': 'Catalog',
  'allowance-params': 'AllowanceParams',
  'system-settings': 'SystemSettings',
  'roles-tenants': 'RolesTenants',
}

/**
 * section → platform roles with Read-or-better access.
 * Mirrors backend `section_access` (platform_rbac.rs). `owner` is implicitly Full
 * everywhere; it is listed explicitly so the map is self-contained.
 */
export const SECTION_ROLES: Record<ConsoleSectionId, string[]> = {
  'overview': ['observer', 'support', 'finance', 'ops', 'owner'],
  'my-clients': ['observer', 'support', 'finance', 'ops', 'owner'],
  'monitoring': ['observer', 'support', 'finance', 'ops', 'owner'],
  'ops-users': ['support', 'ops', 'owner'],
  'finance': ['finance', 'owner'],
  'perf-audit': ['observer', 'finance', 'owner'],
  'strategy': ['ops', 'owner'],
  'controlled-ops': ['finance', 'ops', 'owner'],
  'infra': ['ops', 'owner'],
  'catalog': ['ops', 'owner'],
  'allowance-params': ['finance', 'owner'],
  'system-settings': ['ops', 'owner'],
  'roles-tenants': ['owner'],
}

/**
 * Ordered longest-prefix table: route path → owning Console section.
 * The router stamps `meta.section` from this table (see router/index.ts) so no
 * protected page can be silently un-gated, and the U3 permission-matrix evidence
 * script asserts every named Console page resolves to exactly one section.
 *
 * `/operations` and `/settings` are multi-section hubs, so their leaves are keyed
 * individually. Order matters only in that a longer prefix must win; the resolver
 * sorts by descending prefix length before matching, so declaration order here is
 * for readability, not correctness.
 */
export const SECTION_ROUTE_TABLE: Array<[string, ConsoleSectionId]> = [
  ['/dashboard', 'overview'],
  ['/clients', 'my-clients'],
  ['/monitoring', 'monitoring'],
  ['/perf-audit', 'perf-audit'],
  ['/print', 'perf-audit'],
  ['/controlled-ops', 'controlled-ops'],
  ['/finance', 'finance'],
  // /operations hub — split by leaf
  ['/operations/users', 'ops-users'],
  ['/operations/user-dashboard', 'ops-users'],
  ['/operations/tickets', 'ops-users'],
  ['/operations/resellers', 'ops-users'],
  ['/operations/salesman', 'ops-users'],
  ['/operations/referral', 'ops-users'],
  ['/operations/contracts', 'ops-users'],
  ['/operations/strategies', 'strategy'],
  ['/operations/strategy-dev', 'strategy'],
  ['/operations/vendors', 'strategy'],
  ['/operations/sip', 'strategy'],
  // /settings hub — split by leaf
  ['/settings/roles-tenants', 'roles-tenants'],
  ['/settings/roles', 'roles-tenants'],
  ['/settings/groups', 'roles-tenants'],
  ['/settings/tenants', 'roles-tenants'],
  ['/settings/erp', 'roles-tenants'],
  ['/settings/holidays', 'system-settings'],
  ['/settings/email', 'system-settings'],
  ['/settings/settings', 'system-settings'],
  ['/settings/surveycake', 'system-settings'],
  ['/settings/tokens', 'system-settings'],
  ['/settings/brokers', 'infra'],
  ['/settings/broker-accounts', 'infra'],
  ['/settings/accounts', 'infra'],
  ['/settings/speedy-acc-pass', 'infra'],
  ['/settings/hosts', 'infra'],
  ['/settings/colo', 'infra'],
  ['/settings/upload-cert', 'infra'],
  ['/settings/trading-ip', 'infra'],
  ['/settings/symbols', 'catalog'],
  ['/settings/exchanges', 'catalog'],
  ['/settings/asset-classes', 'catalog'],
  ['/settings/prod-groups', 'catalog'],
  ['/settings/calendar', 'catalog'],
  ['/settings/real-symbols', 'catalog'],
  ['/settings/symbol-events', 'catalog'],
  ['/settings/coupons', 'allowance-params'],
  ['/settings/level', 'allowance-params'],
  ['/settings/param', 'allowance-params'],
]

// Precomputed longest-prefix-first ordering (stable, module-load-time).
const SORTED_TABLE = [...SECTION_ROUTE_TABLE].sort((a, b) => b[0].length - a[0].length)

/**
 * Resolve a route path to its Console section via longest-prefix match.
 * Returns null for paths that are not gated Console pages (login, /403, /404).
 * A prefix matches only on a path-segment boundary so `/settings/rolesX` does not
 * match `/settings/roles`.
 */
export function resolveConsoleSection(path: string): ConsoleSectionId | null {
  // Normalize: strip query/hash and trailing slash (except root).
  let p = path.split('?')[0].split('#')[0]
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
  for (const [prefix, section] of SORTED_TABLE) {
    if (p === prefix || p.startsWith(prefix + '/')) return section
  }
  return null
}

/**
 * Whether the given platform roles grant access to a section.
 *
 * Fail-closed: an unknown section id → false. Empty platform roles (a platform
 * token whose `platform_roles` claim is empty) are treated by the backend as
 * `observer` (fail-closed default), so we mirror that here rather than granting
 * access. `site_admin` / `site_owner` WITHOUT any platform role is a legacy shape
 * still present in the auth store; we grant it only for sections observer can see,
 * matching the backend which would give such a token observer access.
 */
export function hasSectionAccess(
  platformRoles: string[],
  sectionId: ConsoleSectionId | null,
  primaryRole?: string | null,
): boolean {
  if (!sectionId) return false
  const allowed = SECTION_ROLES[sectionId]
  if (!allowed) return false

  // Owner short-circuit (explicit platform owner role).
  if (platformRoles.includes('owner')) return true

  if (platformRoles.length > 0) {
    return allowed.some(role => platformRoles.includes(role))
  }

  // Legacy: platform token with no platform_roles claim → backend treats as
  // observer (fail-closed). Mirror observer access exactly.
  if (primaryRole === 'site_admin' || primaryRole === 'site_owner') {
    return allowed.includes('observer')
  }

  return false
}
